// @file Surface drafts store
import { ActionHistoryType, recordActionHistory } from '@@/bits/action_history_manager'
import { trackEvent } from '@@/bits/analytics'
import { isAppUsing } from '@@/bits/flip'
import { __ } from '@@/bits/intl'
import { POST_BODY_CHARACTER_LIMIT, POST_SUBJECT_CHARACTER_LIMIT } from '@@/bits/numbers'
import { partitionedDebounce } from '@@/bits/partitioned_debounce'
import { isPhotoAlbum } from '@@/bits/post_attachment'
import { addDraftCidIfAbsent, getAttachmentsFromDraft, shouldSyncDraftUpdates } from '@@/bits/post_drafts'
import { hasSameCustomProperties, isPostEmpty } from '@@/bits/post_properties'
import PromiseQueue from '@@/bits/promise_queue'
import { getPollFromPost } from '@@/bits/surface_polls'
import type { UploadJob, UploadPurpose } from '@@/bits/uploader'
import { vDel, vSet } from '@@/bits/vue'
import { SnackbarNotificationType, useGlobalSnackbarStore } from '@@/pinia/global_snackbar'
import { useNativeAppStore } from '@@/pinia/native_app'
import { usePostComposerModalStore } from '@@/pinia/post_composer_modal_store'
import { useSurfaceStore } from '@@/pinia/surface'
import { useSurfaceAttachmentsStore } from '@@/pinia/surface_attachments'
import { ContributionType, useSurfaceGuestStore } from '@@/pinia/surface_guest_store'
import { useSurfacePermissionsStore } from '@@/pinia/surface_permissions'
import { useSurfacePostsStore } from '@@/pinia/surface_posts'
import { useSurfacePostPropertiesStore } from '@@/pinia/surface_post_properties'
import { useSurfaceSectionsStore } from '@@/pinia/surface_sections'
import { useSurfaceShareLinksStore } from '@@/pinia/surface_share_links'
import { WishDraftApi } from '@@/surface/api/wish_draft'
import type { AttachmentProps, Cid, DraftPost, Id, Post, PostAttributes, WishAttachment } from '@@/types'
import { usePostBuilder } from '@@/vuecomposables/surface_post_builder'
import { useUndoable } from '@@/vuecomposables/undoable'
import { useSnackbarPublishProgress } from '@@/vuecomposables/useMultipleDraftPublishProgress'
import { usePostAttachmentUploader } from '@@/vuecomposables/usePostAttachmentUploader'
import { hasAttachment } from '@@/vuexstore/helpers/post'
import { cloneDeep, isEqual, omitBy } from 'es-toolkit'
import { isEmpty, uniqueId } from 'es-toolkit/compat'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

enum DraftSaveState {
  Unsaved = 'unsaved',
  Saved = 'saved',
  Creating = 'creating',
  Updating = 'updating',
  Deleting = 'deleting',
}

interface DraftSyncState {
  [cid: Cid]:
    | {
        state: DraftSaveState
        loading: boolean
        error: boolean
      }
    | undefined
}
const DEBOUNCE_WAIT = 1500
const DRAFT_SYNC_DEBOUNCE_TIMERS = new Map<string, number>()
function clearDraftSyncDebounceTimer(cid: Cid): void {
  clearTimeout(DRAFT_SYNC_DEBOUNCE_TIMERS.get(cid))
}

const autoSaveQueueKey = (cid: Cid): string => {
  return `syncDraft:${cid}`
}

export const useSurfaceDraftsStore = defineStore('surfaceDrafts', () => {
  const surfacePostsStore = useSurfacePostsStore()
  const surfaceStore = useSurfaceStore()
  const globalSnackbarStore = useGlobalSnackbarStore()
  const surfacePermissionsStore = useSurfacePermissionsStore()
  const surfaceSectionsStore = useSurfaceSectionsStore()
  const surfacePostPropertiesStore = useSurfacePostPropertiesStore()
  const surfaceGuestStore = useSurfaceGuestStore()
  const postComposerModalStore = usePostComposerModalStore()
  const surfaceAttachmentsStore = useSurfaceAttachmentsStore()
  const surfaceShareLinksStore = useSurfaceShareLinksStore()

  const { startUploadFile, stopUploadFile } = usePostAttachmentUploader()
  const { setDeletedSnackbar } = useUndoable()

  // #region Draft Getters
  const draftByCid = ref<Record<Cid, DraftPost>>({})
  const draftById = computed<Record<Id, DraftPost>>(() =>
    Object.values(draftByCid.value).reduce<Record<Id, DraftPost>>((accumm, draft) => {
      if (draft.id != null) {
        accumm[draft.id] = draft
      }
      return accumm
    }, {}),
  )

  const filteredDraftsByCid = computed<Record<Cid, DraftPost>>(() => {
    return Object.values(draftByCid.value).reduce<Record<Cid, DraftPost>>((accumm, draft) => {
      if (surfaceShareLinksStore.isValidPostOrDraftForBreakout(draft)) {
        accumm[draft.cid] = draft
      }
      return accumm
    }, {})
  })

  const filteredDraftsById = computed(() => {
    return Object.values(filteredDraftsByCid.value).reduce<Record<Id, DraftPost>>((accumm, draft) => {
      if (draft.id != null) {
        accumm[draft.id] = draft
      }
      return accumm
    }, {})
  })

  const draftCids = computed<Cid[]>(() => {
    // with lodash, this used to be
    // const cids = sortBy(
    //   Object.keys(draftByCid.value),
    //   (postCid) => -1 * new Date(draftByCid.value[postCid].created_at ?? 0).valueOf(),
    // )
    // however, at the time this is written, es-toolkit sortBy doesn't support string[]: https://github.com/toss/es-toolkit/issues/869
    const cids = Object.keys(filteredDraftsByCid.value).sort((a, b) => {
      const dateA = new Date(filteredDraftsByCid.value[a].created_at ?? 0).valueOf()
      const dateB = new Date(filteredDraftsByCid.value[b].created_at ?? 0).valueOf()
      return dateB - dateA // Sort in descending order (newest first)
    })

    return cids
  })

  function isCidBeingDrafted(cid: Cid): boolean {
    return draftByCid.value[cid] != null
  }
  function getDraftFromCid(cid: Cid): DraftPost | null {
    return draftByCid.value[cid] ?? null
  }

  const getAttachmentPropsFromDraft = (draftCid: Cid): AttachmentProps | null => {
    return draftByCid.value[draftCid]?.wish_content?.attachment_props ?? null
  }
  // #endregion

  // #region Active Draft States
  const activeDraft = computed<DraftPost | null>(() =>
    surfacePostsStore.postBeingEditedCid != null ? draftByCid.value[surfacePostsStore.postBeingEditedCid] : null,
  )
  const activeDraftCid = computed<Cid | null>(() => activeDraft.value?.cid ?? null)
  const isAnyDraftActive = computed<boolean>(() => activeDraftCid.value != null)
  const doesActiveDraftHaveAttachment = computed<boolean>(
    () => activeDraft.value != null && hasAttachment(activeDraft.value),
  )
  const activeDraftAttachments = computed<WishAttachment[]>(() =>
    activeDraft.value != null ? getAttachmentsFromDraft(activeDraft.value) : [],
  )
  const activeDraftHasPhotoAlbum = computed<boolean>(() => activeDraft.value != null && isPhotoAlbum(activeDraft.value))
  const activeDraftHasSingleImageAttachment = computed<boolean>(() => {
    const isActiveDraftAttachmentPhoto =
      activeDraft.value?.attachment != null &&
      activeDraft.value.attachment !== '' &&
      surfaceAttachmentsStore.getLinkAttributesForUrl(activeDraft.value.attachment)?.content_category === 'photo'

    return !activeDraftHasPhotoAlbum.value && doesActiveDraftHaveAttachment.value && isActiveDraftAttachmentPhoto
  })
  const isActiveDraftNewPost = computed<boolean>(() =>
    activeDraft.value == null ? false : !surfacePostsStore.isExistingPost(activeDraft.value.cid),
  )

  const activeDraftUploadingFile = computed<File | undefined>(
    () =>
      activeDraft.value?.uploadingFile ??
      (activeDraftAttachments.value.length === 1 ? activeDraftAttachments.value[0].fileBeingUploaded : undefined),
  )
  const activeDraftUploadingFileProgress = computed<number | undefined>(
    () =>
      activeDraft.value?.uploadProgress ??
      (activeDraftAttachments.value.length === 1 ? activeDraftAttachments.value[0]?.progress : undefined),
  )
  const activeDraftIsUploadingFile = computed<boolean>(() => activeDraftUploadingFile.value != null)

  function isCidActiveDraft(cid: Cid): boolean {
    return cid != null && activeDraftCid.value === cid
  }
  // #endregion

  // #region Edit Drafts
  const postOnlyHasDefaultValues = (cid: Cid): boolean => {
    // remove all default values
    // if post is empty after then post only had default values
    const currentDraft = cloneDeep(draftByCid.value[cid])
    if (currentDraft == null) return false

    // remove default values from custom properties
    const customPropertiesNoDefaultValues = omitBy(currentDraft.custom_properties ?? {}, (value, key) => {
      if (surfacePostPropertiesStore.wallSingleSelectDefaultsByCustomPropertyId[key] === value) {
        return true
      }
      return false
    })
    currentDraft.custom_properties = customPropertiesNoDefaultValues
    return isPostEmpty(currentDraft)
  }

  const isDraftDirty = (cid: Cid): boolean => {
    const currentDraft = draftByCid.value[cid]
    if (currentDraft == null) return false
    const currentDraftEntity = {
      subject: currentDraft?.subject,
      body: currentDraft?.body,
      attachment: currentDraft?.attachment,
      attachment_caption: currentDraft?.attachment_caption,
      alternative_text: currentDraft?.wish_content?.attachment_props?.alternative_text,
      scheduled_at: currentDraft?.scheduled_at,
      poll: currentDraft != null ? getPollFromPost(currentDraft) : null,
    }
    const existingPost = surfacePostsStore.rawPostEntitiesByCid[cid]
    const existingPostEntity: typeof currentDraftEntity = {
      subject: existingPost?.subject,
      body: existingPost?.body,
      attachment: existingPost?.attachment,
      attachment_caption: existingPost?.attachment_caption,
      alternative_text: existingPost?.wish_content?.attachment_props?.alternative_text,
      scheduled_at: existingPost?.scheduled_at,
      poll: existingPost != null ? getPollFromPost(existingPost) : null,
    }
    return (
      !isEqual(currentDraftEntity, existingPostEntity) ||
      !hasSameCustomProperties(currentDraft, existingPost ?? {}) ||
      currentDraft?.uploadingFile != null
    )
  }

  function startEditingDraft(draftCid: Cid): void {
    // startEditingPost works on both drafts and posts, depending on their cid
    // if cid is draft, it is set as the active draft. if post, it copies an existing post and sets it to the active draft.
    surfacePostsStore.startEditingPost({ postCid: draftCid })
  }

  // stop editing draft, but doesn't discard it. for eg when minimizing composer
  function stopEditingDraft(): void {
    surfacePostsStore.stopEditingPost()
  }

  // handler for when a user clicks "close" on the composer. discards draft currently being edited
  async function cancelEditingDraft(options?: { trackHistory?: boolean; softDelete?: boolean }): Promise<void> {
    if (activeDraftCid.value == null) return
    await removeDraft(activeDraftCid.value, options)
  }

  function removeDraftAttachment(postCid: Cid): void {
    if (draftByCid.value[postCid] == null) return
    updateDraft({
      cid: postCid,
      attachment: '',
      attachment_caption: null,
      attachment_link: null,
      attachments: undefined,
    })
    if (draftByCid.value[postCid].wish_content != null) {
      const post = draftByCid.value[postCid]
      vSet(draftByCid.value, postCid, {
        ...post,
        wish_content: {
          ...post.wish_content,
          attachment_props: null,
        },
        attachment_link: null,
      })
    }
  }

  function removeDraftCustomProperties(postCid: Cid): void {
    if (draftByCid.value[postCid] == null) return
    updateDraft({
      cid: postCid,
      custom_properties: null,
      hasCustomPropertyErrors: false,
      location_name: undefined,
      location_point: undefined,
    })
  }

  function setDraftScheduledAt(cid: Cid, scheduledAt: string): void {
    updateDraft({
      cid,
      scheduled_at: new Date(scheduledAt).toISOString(),
    })
  }

  function removeDraftScheduledAt(cid: Cid): void {
    updateDraft({
      cid,
      scheduled_at: null,
    })
  }

  function uploadFileActiveDraft({
    file,
    maxFileUploadSize,
    trimVideoAfterUpload,
    uploadPurpose,
  }: {
    file: File
    maxFileUploadSize?: number
    trimVideoAfterUpload?: { start: number; end: number }
    uploadPurpose?: UploadPurpose
  }): void {
    if (activeDraftCid.value == null) return
    startUploadFileInDraft({ file, maxFileUploadSize, cid: activeDraftCid.value, trimVideoAfterUpload, uploadPurpose })
  }

  function stopUploadFileActiveDraft(): void {
    if (activeDraftCid.value == null) return
    stopUploadFileInDraft(activeDraftCid.value)
  }

  const autosaveDraftToBeUpdated = (newDraft: DraftPost, existingDraft: DraftPost): void => {
    if (
      surfacePostsStore.isExistingPost(newDraft.cid) || // draft of existing post, don't need to autosave
      (!surfaceGuestStore.shouldEnableAnonymousAttribution && !surfacePermissionsStore.amIRegistered) // Only registered users get saved drafts
    ) {
      return
    }

    if (autoSaveQueueByCid.value[newDraft.cid] == null) {
      vSet(autoSaveQueueByCid.value, newDraft.cid, new PromiseQueue())
    }

    if (draftSyncState.value[newDraft.cid] == null) {
      vSet(draftSyncState.value, newDraft.cid, {
        state: newDraft.id == null ? DraftSaveState.Unsaved : DraftSaveState.Saved,
        loading: false,
        error: false,
      })
    }

    if (shouldSyncDraftUpdates({ newDraft, existingDraft })) {
      debouncedSyncDraft(newDraft)
    }

    // We also need to send surface state when performing autosave
    void useNativeAppStore().postSurfaceState()
  }

  function updateDraft(post: Post | DraftPost | PostAttributes): void {
    if (post.cid == null) return

    const existingDraft = draftByCid.value[post.cid]
    const newDraft = { ...existingDraft, ...post }
    if (newDraft.attachments != null && newDraft.attachments.length >= 1) {
      newDraft.attachment = newDraft.attachments[0].url
    }

    // Always assign a section to the draft.
    if (newDraft.wall_section_id == null) {
      newDraft.wall_section_id =
        surfaceSectionsStore.mostRecentlyTouchedSectionId ?? surfaceSectionsStore.defaultSectionId
    }

    // At the same time, we need to set the wall_section_hashid
    const sectionHashid = surfaceSectionsStore.getSectionById(newDraft.wall_section_id ?? null)?.hashid
    if (sectionHashid != null) {
      newDraft.wall_section_hashid = sectionHashid
    }

    if (newDraft.draftCreatedAt == null) {
      newDraft.draftCreatedAt = Date.now()
    }

    vSet(draftByCid.value, newDraft.cid, newDraft)

    autosaveDraftToBeUpdated(newDraft, existingDraft)
  }

  function updateActiveDraft(post: Post | Partial<DraftPost> | PostAttributes): void {
    updateDraft({ ...activeDraft.value, ...post, cid: activeDraft.value?.cid ?? post.cid })
  }

  const updateDraftWishContentAttachmentProps = ({
    cid,
    url,
    attachmentProps,
  }: {
    cid: Cid
    url: string
    attachmentProps?: AttachmentProps
  }): void => {
    if (!isCidBeingDrafted(cid)) return
    if (attachmentProps != null) {
      updateDraft({
        cid,
        wish_content: {
          is_processed: true,
          attachment_props: attachmentProps,
        },
      })
    } else {
      updateDraft({
        cid,
        wish_content: {
          is_processed: false,
          attachment_props: { url },
        },
      })
    }
  }

  const startUploadFileInDraft = ({
    file,
    maxFileUploadSize,
    cid,
    trimVideoAfterUpload,
    uploadPurpose,
  }: {
    file: File
    maxFileUploadSize?: number
    cid: Cid
    trimVideoAfterUpload?: { start: number; end: number }
    uploadPurpose?: UploadPurpose
  }): void => {
    // if there is not custom maxFileUploadSize, the default limit based on user membership is used
    const maxUploadSize = maxFileUploadSize ?? surfaceStore.uploadLimit
    updateDraft({ cid, maxFileUploadSize: maxUploadSize, uploadingFile: file, uploadProgress: 0 })
    startUploadFile({ file, maxFileUploadSize: maxUploadSize, cid, trimVideoAfterUpload, uploadPurpose })
  }

  function stopUploadFileInDraft(cid: Cid): void {
    updateDraft({ cid, uploadingFile: null })
    stopUploadFile({ cid })
  }

  const replaceActiveDraftUploadingFile = ({ file }: { file: File }): void => {
    if (activeDraft.value?.attachment != null) {
      // The previous attachment upload/adding has finished, remove the existing attachment
      removeDraftAttachment(activeDraft.value.cid)
    } else if (activeDraftUploadingFile.value != null) {
      // Another file is being uploaded, stop that upload first
      stopUploadFileActiveDraft()
    }

    uploadFileActiveDraft({ file })
  }

  const replaceActiveDraftAttachmentWithLink = ({ link }: { link: string }): void => {
    // replace the active draft's attachment/uploading file when a link is dropped on to it
    if (activeDraft.value?.attachment != null) {
      // An attachment already exists or a file upload is done => remove it
      removeDraftAttachment(activeDraft.value.cid)
    } else if (activeDraftUploadingFile.value != null) {
      // A file is being uploaded, stop that upload first
      stopUploadFileActiveDraft()
    }

    updateActiveDraft({
      attachment: link,
      wish_content: {
        attachment_props: {
          url: link,
        },
        is_processed: false,
      },
    })
  }

  const reassignSections = ({ oldSectionId, newSectionId }: { oldSectionId: Id; newSectionId: Id }): void => {
    const draftsWithOldSection = Object.values(draftByCid.value).filter(
      (draft) => draft.wall_section_id === oldSectionId,
    )
    draftsWithOldSection.forEach((draft) => {
      updateDraft({
        cid: draft.cid,
        wall_section_id: newSectionId,
      })
    })
  }
  // #endregion

  // #region Draft attachment video trimming upload. Used in usePostAttachmentUploader.ts
  const uploadJobs = ref<Record<string, UploadJob>>({})
  const getUploadJob = (cid: Cid, attachmentKey?: string): UploadJob | undefined =>
    uploadJobs.value[attachmentKey != null ? `${cid}-${attachmentKey}` : cid]
  const setUploadJob = ({
    cid,
    uploadJob,
    attachmentKey,
  }: {
    cid: Cid
    uploadJob: UploadJob
    attachmentKey?: string
  }): void => {
    vSet(uploadJobs.value, attachmentKey != null ? `${cid}-${attachmentKey}` : cid, uploadJob)
  }
  const deleteUploadJob = ({ cid, attachmentKey }: { cid: Cid; attachmentKey?: string }): void => {
    vDel(uploadJobs.value, attachmentKey != null ? `${cid}-${attachmentKey}` : cid)
  }
  const isUploadingAndTrimmingVideoInDraft = (cid: Cid): boolean => {
    const uploadJob = uploadJobs.value[cid]
    if (uploadJob == null) return false
    return uploadJob.shouldTrimVideoAfterUpload
  }
  // #endregion

  // #region Publish Drafts
  // useSnackbarPublishProgress handles the displayed progress of multiple draft publish attempts.
  const { startTrackingStatus, markStatusAsPublished, markStatusAsFailed, stopTrackingStatus, resetStatus } =
    useSnackbarPublishProgress(draftByCid)

  const publishDraft = async ({ cid }: { cid: Cid }): Promise<void> => {
    if (surfaceGuestStore.shouldShowGuestIdModal) {
      if (surfaceStore.isSubmissionRequest) {
        postComposerModalStore.minimizeAllComposerModals()
      }
      // Sync draft to make sure the draft has been synced and then it has an id
      await syncDraft(draftByCid.value[cid])
      surfaceGuestStore.showGuestIdModal({
        afterSaveActions: [async () => await publishDraft({ cid })],
        contributionType: ContributionType.DraftPost,
        contributionPayload: getDraftFromCid(cid) as DraftPost,
      })
      // This is not an error, so we can skip recording it as a failed attempt
      stopTrackingStatus(cid)
      return
    } else if (surfaceGuestStore.shouldUpdateNameBeforePublishing) {
      surfaceGuestStore.setUserName(surfaceGuestStore.displayName)
      await surfaceGuestStore.updateSessionUserName({ shouldModerate: false })
    }
    const existingDraft = draftByCid.value[cid]
    if (existingDraft == null) {
      stopTrackingStatus(cid)
      return
    }

    if (!surfacePermissionsStore.canIPost) {
      markStatusAsFailed(cid)
      return
    }

    if (!areRequiredFieldsPresent(existingDraft)) {
      globalSnackbarStore.setSnackbar(
        {
          message: __('Required fields must be filled to publish post'),
          notificationType: SnackbarNotificationType.error,
        },
        {
          allowDuplicate: false,
        },
      )
      markStatusAsFailed(cid)
      return
    }
    const existingPost = surfacePostsStore.rawPostEntitiesByCid[cid]

    /**
     * We compare existingDraft with existingPost so only posts that already exceed character limit
     * can continue to exceed character limit
     */

    if (
      (existingPost?.subject == null || existingPost?.subject.length <= POST_SUBJECT_CHARACTER_LIMIT) &&
      existingDraft.subject != null &&
      existingDraft.subject.length > POST_SUBJECT_CHARACTER_LIMIT
    ) {
      globalSnackbarStore.setSnackbar(
        {
          message: __('Post subject character limit reached'),
          notificationType: SnackbarNotificationType.error,
        },
        {
          allowDuplicate: false,
        },
      )
      markStatusAsFailed(cid)
      return
    }

    if (
      (existingPost?.body == null || existingPost?.body.length <= POST_BODY_CHARACTER_LIMIT) &&
      existingDraft.body != null &&
      existingDraft.body.length > POST_BODY_CHARACTER_LIMIT
    ) {
      globalSnackbarStore.setSnackbar(
        {
          message: __('Post body character limit reached'),
          notificationType: SnackbarNotificationType.error,
        },
        {
          allowDuplicate: false,
        },
      )
      markStatusAsFailed(cid)
      return
    }

    // If a file is being uploaded in the draft, set it to auto publish
    if (
      existingDraft.uploadingFile != null ||
      (existingDraft.attachments?.some((a) => a.isUploadingFile === true) ?? false)
    ) {
      autoPublishDraft({ cid })
    } else if (!isPostEmpty(existingDraft)) {
      markStatusAsPublished(cid)
      void surfacePostsStore.savePost({ attributes: existingDraft })
    } else {
      void surfacePostsStore.deletePost(cid, { trackHistory: false, softDelete: false })
      stopTrackingStatus(cid)
    }
  }

  const areRequiredFieldsPresent = (draft: DraftPost): boolean => {
    const requiredPostFields = surfacePostPropertiesStore.requiredPostProperties
    const requiredCustomProps = surfacePostPropertiesStore.requiredWallCustomPostPropertiesById
    const requiredCustomPropsIds = Object.keys(requiredCustomProps)
    const validSubject = requiredPostFields.subject ? !isEmpty(draft.subject?.trim()) : true
    const validBody = requiredPostFields.body ? !isEmpty(draft.body) : true
    const validAttachment = requiredPostFields.attachment !== false ? hasAttachment(draft) : true

    const draftCustomProps = Object.entries(draft.custom_properties ?? {})
    // check every required custom prop is present and the value is not null
    const validCustomProps =
      requiredCustomPropsIds.length > 0
        ? requiredCustomPropsIds.every((id) =>
            draftCustomProps.some(([propId, propValue]) => propId === id && propValue != null),
          )
        : true

    return validSubject && validBody && validAttachment && validCustomProps
  }

  const autoPublishDraft = ({ cid }: { cid: Cid }): void => {
    vSet(draftByCid.value[cid], 'shouldAutoPublish', true)
  }

  function stopAutoPublishDraft(cid: Cid): void {
    vSet(draftByCid.value[cid], 'shouldAutoPublish', false)
  }

  const showPublishAllDraftsPopover = ref(false)

  function stopAutoPublishAllDrafts(): void {
    for (const cid of draftCids.value) {
      stopAutoPublishDraft(cid)
    }

    // We reset the publish attempts since the publishing of the posts
    // have stopped and we also don't need to show the snackbar anymore
    resetStatus()
  }

  async function publishAllPostDrafts(): Promise<void> {
    // reverse to publish the oldest draft first
    // need to reverse on a separate line: https://sonarcloud.io/organizations/padlet/rules?open=typescript%3AS4043&rule_key=typescript%3AS4043
    const reversedDraftCids = [...draftCids.value].reverse()

    await publishSelectedDrafts(reversedDraftCids)
  }

  async function publishSelectedDrafts(draftCids: Cid[]): Promise<void> {
    draftCids.forEach((cid) => {
      startTrackingStatus(cid)
      void publishDraft({ cid })
    })
  }
  // #endregion

  // #region Create Drafts
  /**
   * Inserts a new draft post without triggering autosave.
   * For autosaving, use updateDraft
   * @param newDraft - The draft post to be inserted.
   */
  function insertDraft(newDraft: DraftPost): void {
    // Don't insert a draft if it already exists
    if (newDraft.id != null && draftById.value[newDraft.id] != null) return

    newDraft = addDraftCidIfAbsent(newDraft)
    newDraft.draftCreatedAt = Date.now()
    vSet(autoSaveQueueByCid.value, newDraft.cid, new PromiseQueue())
    vSet(draftSyncState.value, newDraft.cid, {
      state: newDraft.id == null ? DraftSaveState.Unsaved : DraftSaveState.Saved,
      loading: false,
      error: false,
    })
    vSet(draftByCid.value, newDraft.cid, newDraft)
    setDraftNumber(newDraft.cid)
  }

  const latestPendingDraftCid = ref<Cid | null>(null)

  /**
   * This function builds the post with its given attributes, assigns a suitable wall section, and also starts uploading the draft file if any.
   *
   * @param {Object} options - The options for starting a new draft.
   * @param {Post | DraftPost | PostAttributes} options.attributes - The attributes of the post or draft.
   * @param {boolean} options.shouldStartEditing - Indicates whether the draft should be started in editing mode.
   */
  const startNewDraft = ({
    attributes,
    shouldStartEditing,
  }: {
    attributes: Post | DraftPost | PostAttributes
    shouldStartEditing: boolean
  }): { cid: Cid } => {
    const post = usePostBuilder().buildPost(attributes)
    if (post.wall_section_id == null && surfaceSectionsStore.assignedWallSectionId != null) {
      post.wall_section_id = surfaceSectionsStore.assignedWallSectionId
    }

    surfacePostsStore.updatePostInStore({ ...post, subject: '', body: '', attachment: '' })
    updateDraft({ ...post, shouldStartEditing })
    setDraftNumber(post.cid)

    if (shouldStartEditing) {
      surfacePostsStore.startEditingPost({ postCid: post.cid })
    }

    if (attributes.file != null) {
      startUploadFileInDraft({ file: attributes.file, cid: post.cid })
    }

    return { cid: post.cid }
  }

  const createNewDraftFromPendingUpload = ({ file, postAttributes }: { file: File; postAttributes: Post }): void => {
    const newPostAttributes = usePostBuilder().buildPost(postAttributes)
    surfacePostsStore.updatePostInStore({ ...newPostAttributes, subject: '' })

    // Create the draft
    updateDraft({ ...newPostAttributes, subject: postAttributes.subject ?? '' })
    setDraftNumber(newPostAttributes.cid)

    // Start upload
    startUploadFileInDraft({ file, cid: newPostAttributes.cid })

    if (newPostAttributes.cid === latestPendingDraftCid.value) {
      surfacePostsStore.startEditingPost({ postCid: newPostAttributes.cid })
      latestPendingDraftCid.value = null
    }
  }
  // #endregion

  // #region Delete Drafts
  const autosaveDraftToBeDeleted = async (
    postCid: Cid,
    options?: { trackHistory?: boolean; softDelete?: boolean },
  ): Promise<void> => {
    // a previous operation (create/update) may be debounced, enqueued or executing.
    // clear debounce and queue, then enqueue a delete operation which deletes the draft and clears autosave state
    clearDraftSyncDebounceTimer(postCid)
    const autoSaveQueue = autoSaveQueueByCid.value[postCid]
    autoSaveQueue?.clear()
    await autoSaveQueue?.enqueue(autoSaveQueueKey(postCid), async function deletePostDraft() {
      const draft = draftByCid.value[postCid]
      if (draft.id != null) {
        void WishDraftApi.delete(draft.id, {
          softDelete: isAppUsing('undoRedoDeleteActions') && options?.softDelete === true,
        })
      }
      vDel(autoSaveQueueByCid.value, postCid)
      vDel(draftSyncState.value, postCid)
      vDel(draftByCid.value, postCid)
      void trackEvent('Post drafts', 'Deleted draft')
      void useNativeAppStore().postSurfaceState()
      if (isAppUsing('undoRedoDeleteActions') && !isPostEmpty(draft) && options?.trackHistory === true) {
        const actionHistoryId = recordActionHistory(surfaceStore.wallHashid, ActionHistoryType.DELETE_DRAFTS, [draft])
        setDeletedSnackbar(ActionHistoryType.DELETE_DRAFTS, actionHistoryId, [draft])
      }
    })
  }

  const removeDraft = async (
    postCid: Cid,
    options?: { trackHistory?: boolean; softDelete?: boolean },
  ): Promise<void> => {
    if (postCid === activeDraftCid.value) {
      stopUploadFileActiveDraft()
      surfacePostsStore.stopEditingPost()
    }

    if (
      surfacePostsStore.isExistingPost(postCid) || // removing a draft of an existing post -> it's not a saved draft, simply remove it from drafts store
      (!surfaceGuestStore.shouldEnableAnonymousAttribution && !surfacePermissionsStore.amIRegistered) // Only registered users get saved drafts
    ) {
      vDel(draftByCid.value, postCid)
      return
    }
    await autosaveDraftToBeDeleted(postCid, options)
  }

  // wraps vuex removeDraft function. Removes a draft given its cid.
  // Calls delete draft API.
  // Optionally removes corresponding post
  async function removeDraftAndDeleteNonExistingPost(cid: Cid): Promise<void> {
    await removeDraft(cid, { trackHistory: true, softDelete: true })
    if (!surfacePostsStore.isExistingPost(cid)) {
      // remove draft that's not saved as post yet -> remove corresponding entity in posts store.
      await surfacePostsStore.deletePost(cid, { trackHistory: false, softDelete: false })
    }
  }

  async function removeAllPostDrafts(): Promise<void> {
    await removePostDrafts(draftCids.value, { trackHistory: true })
  }

  async function removePostDrafts(cids: Cid[], options?: { trackHistory?: boolean }): Promise<void> {
    const shouldTrackHistory = isAppUsing('undoRedoDeleteActions') && options?.trackHistory === true
    if (!shouldTrackHistory) {
      await Promise.all(
        cids.map(async (cid) => {
          await removeDraft(cid, { trackHistory: false })
        }),
      )
      return
    }

    const drafts: DraftPost[] = []
    await Promise.all(
      cids.map(async (cid) => {
        const draft = draftByCid.value[cid]
        if (draft != null && !isPostEmpty(draft)) {
          drafts.push(draft)
        }
        // we want to track history separately to consolidate all the drafts
        await removeDraft(cid, { trackHistory: false, softDelete: true })
      }),
    )

    const actionHistoryId = recordActionHistory(surfaceStore.wallHashid, ActionHistoryType.DELETE_DRAFTS, drafts)
    setDeletedSnackbar(ActionHistoryType.DELETE_DRAFTS, actionHistoryId, drafts)
  }

  function resetDrafts(): void {
    draftByCid.value = {}
  }
  // #endregion

  // #region Remote Operations
  function insertDraftRemote(newDraft: DraftPost): void {
    if (newDraft.id == null) return // a saved draft should always have an ID

    const existingDraft = draftById.value[newDraft.id]
    if (existingDraft != null) return // if draft with id already exists, no need to insert

    insertDraft(newDraft)

    void useNativeAppStore().postSurfaceState()
  }

  function updateDraftRemote(draft: DraftPost): void {
    if (draft.id == null) return

    const existingDraft = draftById.value[draft.id]
    if (existingDraft != null) {
      // if draft with id already exists, update it
      const updatedDraft = { ...existingDraft, ...draft }
      vSet(draftByCid.value, updatedDraft.cid, updatedDraft)
    } else {
      // the draft doesn't exist in store yet; insert it instead.
      insertDraft(draft)
    }

    void useNativeAppStore().postSurfaceState()
  }

  function removeDraftRemote(draft: Pick<DraftPost, 'id'>): void {
    if (draft.id == null) return

    const existingDraft = draftById.value[draft.id]
    if (existingDraft != null) {
      vDel(draftByCid.value, existingDraft.cid)
    }

    void useNativeAppStore().postSurfaceState()
  }
  // #endregion

  // #region Fetch Drafts
  const hasFetchedInitialPostDrafts = ref(false)

  // Fetches list of drafts on the wall and inserts into vuex store
  async function fetchPostDrafts(): Promise<void> {
    const postDrafts = await WishDraftApi.list({ wallId: surfaceStore.wallId })
    for (const postDraft of postDrafts) {
      insertDraft(postDraft)
    }
    void useNativeAppStore().postSurfaceState() // mobile app needs knowledge of CIDs that surface has assigned to drafts

    hasFetchedInitialPostDrafts.value = true
  }
  // #endregion

  // #region Minimized Drafts
  // when a minimized draft has no subject, we will use "Draft 1", "Draft 2", ... as its title,
  // this counter is to keep track of the number to be used
  const minimizedDraftPlaceholderTitleCount = ref<number>(0)
  const draftShowingMinimizedContextMenuCid = ref<Cid | null>(null)

  /**
   * set the title for minimized draft to "Draft 1", "Draft 2", ... if necessary
   * @param draftCid - The CID of the draft.
   */
  function setDraftNumber(draftCid?: Cid | null): void {
    if (draftCid == null) return

    const draftPost = draftByCid.value[draftCid]
    if (
      draftPost == null ||
      draftPost.minimizedPlaceholderTitle != null ||
      (draftPost.subject != null && draftPost.subject.trim() !== '')
    ) {
      return
    }

    minimizedDraftPlaceholderTitleCount.value += 1
    const post = draftByCid.value[draftCid]
    vSet(draftByCid.value, post.cid, {
      ...post,
      minimizedPlaceholderTitle: __('Draft %{draftNumber}', {
        draftNumber: minimizedDraftPlaceholderTitleCount.value,
      }),
    })
  }

  const openMinimizedContextMenu = ({ cid }: { cid: Cid }): void => {
    draftShowingMinimizedContextMenuCid.value = cid
  }

  const closeMinimizedContextMenu = (): void => {
    draftShowingMinimizedContextMenuCid.value = null
  }
  // #endregion

  // #region Auto Save
  const autoSaveQueueByCid = ref<Record<Cid, PromiseQueue | undefined>>({})
  const draftSyncState = ref<DraftSyncState>({})

  const isDraftAutoSavePending = (cid: Cid): boolean => {
    // pending debounced sync
    if (DRAFT_SYNC_DEBOUNCE_TIMERS.has(cid)) {
      return true
    }

    // promise queue is not empty
    const autoSaveQueue = autoSaveQueueByCid.value[cid]
    if (autoSaveQueue?.isEmpty === false) {
      return true
    }

    return false
  }

  const createPostDraftForSyncing = async (draft: DraftPost): Promise<void> => {
    const currentSyncState = draftSyncState.value[draft.cid]
    // user may have closed composer and cleared syncState/draft
    if (currentSyncState == null) return

    try {
      vSet(draftSyncState.value, draft.cid, {
        ...currentSyncState,
        state: DraftSaveState.Creating,
      })

      const attributes = await WishDraftApi.create(draft)
      // user may have closed composer and cleared syncState/draft
      if (draftByCid.value[draft.cid] == null) {
        return
      }

      updateDraft({ cid: draft.cid, id: attributes.id })
      vSet(draftSyncState.value, draft.cid, {
        ...currentSyncState,
        state: DraftSaveState.Saved,
      })

      void trackEvent('Post drafts', 'Created post draft')
    } catch (err) {
      vSet(draftSyncState.value, draft.cid, {
        ...currentSyncState,
        state: DraftSaveState.Unsaved,
      })
    }
  }

  const deletePostDraftForSyncing = async (draft: DraftPost): Promise<void> => {
    const currentSyncState = draftSyncState.value[draft.cid]
    // user may have closed composer and cleared syncState/draft
    if (currentSyncState == null) return

    // Draft may not have an id yet if it's still creating. Double check against more updated draft from store.
    const id = draft.id ?? draftByCid.value[draft.cid]?.id

    if (id == null) return

    try {
      vSet(draftSyncState.value, draft.cid, {
        ...currentSyncState,
        state: DraftSaveState.Deleting,
      })

      // do not soft delete as we don't want to recover this draft
      await WishDraftApi.delete(id)

      // user may have closed composer and cleared syncState/draft
      if (draftByCid.value[draft.cid] == null) {
        return
      }

      updateDraft({ cid: draft.cid, id: undefined })

      vSet(draftSyncState.value, draft.cid, {
        ...currentSyncState,
        state: DraftSaveState.Unsaved,
      })
    } catch (err) {
      vSet(draftSyncState.value, draft.cid, {
        ...currentSyncState,
        state: DraftSaveState.Saved,
      })
    }
  }

  const updatePostDraftForSyncing = async (draft: DraftPost): Promise<void> => {
    const currentSyncState = draftSyncState.value[draft.cid]
    // user may have closed composer and cleared syncState/draft
    if (currentSyncState == null) return

    // Draft may not have an id yet if it's still creating. Double check against more updated draft from store.
    const id = draft.id ?? draftByCid.value[draft.cid]?.id

    if (id == null) return

    try {
      vSet(draftSyncState.value, draft.cid, { ...currentSyncState, state: DraftSaveState.Updating })
      const updatedDraft = await WishDraftApi.update(id, draft)

      // user may have closed composer and cleared syncState/draft
      if (draftByCid.value[draft.cid] == null) return

      updateDraft({ cid: draft.cid, updated_at: updatedDraft.updated_at })
      vSet(draftSyncState.value, draft.cid, { ...currentSyncState, state: DraftSaveState.Saved })
    } catch (err) {
      vSet(draftSyncState.value, draft.cid, { ...currentSyncState, state: DraftSaveState.Saved })
    }
  }

  /**
    State machine: https://whimsical.com/post-drafts-diagrams-QVWje6U8d4TMm9xGhbDfnK@7YNFXnKbZAHvWYYvTex5e

    This should allow us to handle some edge cases,
    Eg: if user makes updates while the draft is still being created,
    this makes sure the next queued operation is an update instead of another create.
    Similarly if the draft is being deleted and the user makes update to the draft,
    then the next enqueued operation would be a create.
    @param draft - The draft to sync.
  */
  const syncDraft = async (draft: DraftPost): Promise<void> => {
    const syncState = draftSyncState.value[draft.cid]
    if (syncState == null) {
      return
    }

    const draftSaverQueue = autoSaveQueueByCid.value[draft.cid]
    if (draftSaverQueue == null) {
      return
    }

    switch (syncState.state) {
      case DraftSaveState.Unsaved:
      case DraftSaveState.Deleting:
        if (isPostEmpty(draft)) return
        await draftSaverQueue.enqueue(autoSaveQueueKey[draft.cid], async () => await createPostDraftForSyncing(draft))
        break
      case DraftSaveState.Saved:
      case DraftSaveState.Creating:
      case DraftSaveState.Updating:
        if (isPostEmpty(draft)) {
          draftSaverQueue.clear()
          await draftSaverQueue.enqueue(autoSaveQueueKey[draft.cid], async () => await deletePostDraftForSyncing(draft))
          break
        }

        draftSaverQueue.clear()
        await draftSaverQueue.enqueue(autoSaveQueueKey(draft.cid), async () => await updatePostDraftForSyncing(draft))
    }
  }

  const debouncedSyncDraft = partitionedDebounce(async (draft) => await syncDraft(draft), {
    getKey: (draft) => draft.cid,
    wait: DEBOUNCE_WAIT,
    timers: DRAFT_SYNC_DEBOUNCE_TIMERS,
  })
  // #endregion

  // #region Undo/Redo
  async function restoreDraft({
    draft,
    shouldStartEditing,
  }: {
    draft: DraftPost
    shouldStartEditing?: boolean
  }): Promise<DraftPost> {
    if (!isAppUsing('undoRedoDeleteActions')) return draft

    // Generate a new CID to avoid conflicts with any new drafts
    const newCid = uniqueId('c_restored')
    let restoredDraft: DraftPost
    if (draft.id !== undefined) {
      restoredDraft = await WishDraftApi.restore(draft.id)
      restoredDraft.cid = newCid
      updateDraft({ ...restoredDraft, shouldStartEditing: shouldStartEditing ?? false })
      setDraftNumber(newCid)
      if (shouldStartEditing === true) {
        surfacePostsStore.startEditingPost({ postCid: newCid })
      }
    } else {
      restoredDraft = { ...draft, cid: newCid }
      startNewDraft({ attributes: restoredDraft, shouldStartEditing: shouldStartEditing ?? false })
    }
    return restoredDraft
  }

  const undoDeleteDrafts = async (drafts: DraftPost[]): Promise<DraftPost[]> => {
    if (!isAppUsing('undoRedoDeleteActions')) return drafts

    // If there's only one draft, we start editing it.
    const shouldStartEditing = drafts.length === 1
    return await Promise.all(
      drafts.map(async (draft: DraftPost) => {
        return await restoreDraft({ draft, shouldStartEditing })
      }),
    )
  }

  const redoDeleteDrafts = async (drafts: DraftPost[]): Promise<DraftPost[]> => {
    if (!isAppUsing('undoRedoDeleteActions')) return drafts

    // the drafts might've been updated since the last undo, so we use the latest drafts from the store
    const currentDrafts = drafts
      .map((draft) => {
        if (draft.id != null) {
          return draftById.value[draft.id]
        } else if (draft.cid != null) {
          return draftByCid.value[draft.cid]
        }
        return null
      })
      .filter((draft): draft is DraftPost => draft != null)

    // we specify the cids to avoid removing drafts that are not in the redo stack
    await Promise.all(
      currentDrafts.map(async (draft) => {
        await removeDraft(draft.cid, { trackHistory: false, softDelete: true })
      }),
    )
    return currentDrafts
  }
  // #endregion

  return {
    // getter functions
    isCidBeingDrafted,
    getDraftFromCid,
    isCidActiveDraft,
    postOnlyHasDefaultValues,
    draftByCid: filteredDraftsByCid,
    draftById: filteredDraftsById,
    activeDraft,
    activeDraftCid,
    activeDraftUploadingFile,
    draftCids,
    isActiveDraftNewPost,
    isAnyDraftActive,
    doesActiveDraftHaveAttachment,
    activeDraftAttachments,
    activeDraftHasPhotoAlbum,
    activeDraftHasSingleImageAttachment,
    latestPendingDraftCid,
    autoSaveQueueByCid,
    draftSyncState,
    hasFetchedInitialPostDrafts,
    minimizedDraftPlaceholderTitleCount,
    draftShowingMinimizedContextMenuCid,
    showPublishAllDraftsPopover,
    activeDraftIsUploadingFile,
    activeDraftUploadingFileProgress,

    // actions
    isDraftDirty,
    startEditingDraft,
    stopEditingDraft,
    cancelEditingDraft,
    insertDraft,
    updateDraft,
    updateActiveDraft,
    removeDraft,
    removeDraftAndDeleteNonExistingPost,
    fetchPostDrafts,
    removeAllPostDrafts,
    resetDrafts,
    removeDraftAttachment,
    removeDraftCustomProperties,
    publishAllPostDrafts,
    publishSelectedDrafts,
    setDraftScheduledAt,
    removeDraftScheduledAt,
    undoDeleteDrafts,
    redoDeleteDrafts,

    // Post attachment uploads
    setUploadJob,
    deleteUploadJob,
    getUploadJob,
    isUploadingAndTrimmingVideoInDraft,
    uploadFileActiveDraft,
    stopUploadFileActiveDraft,
    startUploadFileInDraft,
    stopUploadFileInDraft,
    replaceActiveDraftUploadingFile,
    replaceActiveDraftAttachmentWithLink,

    reassignSections,
    updateDraftWishContentAttachmentProps,
    publishDraft,
    autoPublishDraft,
    stopAutoPublishDraft,
    stopAutoPublishAllDrafts,
    insertDraftRemote,
    updateDraftRemote,
    removeDraftRemote,
    setDraftNumber,
    getAttachmentPropsFromDraft,
    openMinimizedContextMenu,
    closeMinimizedContextMenu,
    isDraftAutoSavePending,
    debouncedSyncDraft,
    syncDraft,
    startNewDraft,
    createNewDraftFromPendingUpload,
  }
})
